import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, CircularProgress, useTheme, useMediaQuery, Chip, Alert, Link, Button, Collapse,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  PostAdd as PostAddIcon, CreateNewFolder as CreateNewFolderIcon,
  AccountTree as AccountTreeIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmber as WarningAmberIcon,
  UnfoldMore as UnfoldMoreIcon, UnfoldLess as UnfoldLessIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import imputableService, { tipoCuentaLabel, type ImputablePlanCuentasRow } from '../../services/imputableService';
import noImputableService, { formatNoImputableLabel, type NoImputable } from '../../services/noImputableService';
import companiaService from '../../services/companiaService';
import PDFViewer from '../../components/shared/PDFViewer';
import PlanCuentaImputableDrawer from './components/PlanCuentaImputableDrawer';
import PlanCuentaNoImputableDrawer from './components/PlanCuentaNoImputableDrawer';
import PlanCuentasImportDialog from './components/PlanCuentasImportDialog';
import { exportPlanCuentasExcel } from '../../utils/exportPlanCuentasExcel';

const TIPO_ORDER = ['1', '2', '3', '4', '5', '6', '9'];
const LEVEL_PX = 20;

const TIPO_COLORS: Record<string, string> = {
  '1': '#e3f0fb', '2': '#fce8e6', '3': '#e6f4ea',
  '4': '#fff3e0', '5': '#f3e5f5', '6': '#e0f7fa', '9': '#f5f5f5',
};

function visibleEnLabel(v: string | null | undefined): string {
  const s = v != null ? String(v).trim() : '';
  if (!s || /^ninguno$/i.test(s)) return 'Ninguno';
  if (/^ambos$/i.test(s)) return 'Ambos';
  if (s === 'IVarios' || /^ivarios$/i.test(s)) return 'IVarios';
  if (s === 'EVarios' || /^evarios$/i.test(s)) return 'EVarios';
  return s;
}

function formatoNombreRubroReporte(ni: NoImputable | undefined, nroRubro: number): string {
  const base = (ni?.nombreCuenta || ni?.descripcioncta || `Rubro ${nroRubro}`).trim();
  return base.toUpperCase();
}

// ── Tipos ──────────────────────────────────────────────────────────────────

interface RubroGroup {
  nroRubro: number;
  noImputable: NoImputable | undefined;
  treeDepth: number;                      // profundidad real en el árbol (para indentación)
  imputables: ImputablePlanCuentasRow[];
  children: RubroGroup[];
}

interface TipoGroup {
  tipo: string;
  label: string;
  rubros: RubroGroup[];                   // solo raíces de cada tipo
}

// FlatRow: lista plana en orden DFS para renderizar la tabla
interface FlatRow {
  kind: 'rubro' | 'imputable';
  tipo: string;
  rubroKey: string;                       // tipo-nroRubro
  rg: RubroGroup;
  imp?: ImputablePlanCuentasRow;
  ancestorKeys: string[];                 // rubroKey de todos los ancestros
}

// ── Construcción de jerarquía ──────────────────────────────────────────────

function buildHierarchy(
  imputables: ImputablePlanCuentasRow[],
  noImputables: NoImputable[]
): TipoGroup[] {
  const noImpMap = new Map<number, NoImputable>(noImputables.map((n) => [n.nroRubro, n]));

  // Imputables agrupados por rubro
  const impsByRubro = new Map<number, ImputablePlanCuentasRow[]>();
  for (const imp of imputables) {
    const r = imp.nroRubro ?? 0;
    if (!impsByRubro.has(r)) impsByRubro.set(r, []);
    impsByRubro.get(r)!.push(imp);
  }

  // Tipo inicial: solo rubros que tienen imputables directos
  const tipoMap = new Map<number, string>();
  for (const [r, imps] of Array.from(impsByRubro.entries())) {
    tipoMap.set(r, String(imps[0]?.tipo ?? '').trim() || '?');
  }

  // Propagación bidireccional por convergencia:
  // Si un rubro conoce su tipo → se lo pasa a su padre (totalizaEn) y a sus hijos.
  // Se itera hasta que no haya más cambios (fixpoint).
  let changed = true;
  while (changed) {
    changed = false;
    for (const ni of noImputables) {
      const myTipo = tipoMap.get(ni.nroRubro);
      const parentTipo = ni.totalizaEn ? tipoMap.get(ni.totalizaEn) : undefined;
      // Heredar tipo del padre (propagación hacia abajo)
      if (!myTipo && parentTipo) {
        tipoMap.set(ni.nroRubro, parentTipo);
        changed = true;
      }
      // Propagar tipo al padre (propagación hacia arriba)
      if (myTipo && ni.totalizaEn && ni.totalizaEn !== 0 && !parentTipo) {
        tipoMap.set(ni.totalizaEn, myTipo);
        changed = true;
      }
    }
  }

  // Todos los rubros: unión de no imputables y rubros de imputables
  const allRubros = new Set<number>();
  noImputables.forEach((n) => allRubros.add(n.nroRubro));
  imputables.forEach((i) => { if (i.nroRubro != null) allRubros.add(i.nroRubro); });

  // Asignar cada rubro a su tipo (fallback '?' si no se pudo determinar)
  const tipoRubrosMap = new Map<string, number[]>();
  for (const nr of Array.from(allRubros)) {
    const t = tipoMap.get(nr) ?? '?';
    if (!tipoRubrosMap.has(t)) tipoRubrosMap.set(t, []);
    tipoRubrosMap.get(t)!.push(nr);
  }

  const tiposSorted = Array.from(tipoRubrosMap.keys()).sort((a, b) => {
    const ia = TIPO_ORDER.indexOf(a), ib = TIPO_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1; if (ib === -1) return -1;
    return ia - ib;
  });

  return tiposSorted.map((tipo) => {
    const rubroNros = tipoRubrosMap.get(tipo) ?? [];
    const rubroSet = new Set(rubroNros);

    // Raíces: rubros cuyo padre NO está en este tipo
    const roots: number[] = [];
    const childMapLocal = new Map<number, number[]>();
    for (const nr of rubroNros) {
      const ni = noImpMap.get(nr);
      const parent = ni?.totalizaEn ?? 0;
      // Si totaliza en sí mismo, se trata como raíz para evitar ciclos y ocultamientos.
      if (!parent || parent === nr || !rubroSet.has(parent)) {
        roots.push(nr);
      } else {
        if (!childMapLocal.has(parent)) childMapLocal.set(parent, []);
        childMapLocal.get(parent)!.push(nr);
      }
    }

    // Construir árbol recursivamente con treeDepth
    const buildGroup = (nr: number, depth: number): RubroGroup => {
      const ni = noImpMap.get(nr);
      const imps = [...(impsByRubro.get(nr) ?? [])].sort(
        (a, b) => Number(a.codImputable) - Number(b.codImputable)
      );
      const childNros = [...(childMapLocal.get(nr) ?? [])].sort((a, b) => a - b);
      return {
        nroRubro: nr, noImputable: ni, treeDepth: depth,
        imputables: imps,
        children: childNros.map((c) => buildGroup(c, depth + 1)),
      };
    };

    const rubros = roots.sort((a, b) => a - b).map((r) => buildGroup(r, 0));
    return { tipo, label: tipoCuentaLabel(tipo), rubros };
  });
}

// ── Aplanar árbol en orden DFS ─────────────────────────────────────────────

function flattenRubros(rubros: RubroGroup[], tipo: string, ancestorKeys: string[]): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const rg of rubros) {
    const rubroKey = `${tipo}-${rg.nroRubro}`;
    rows.push({ kind: 'rubro', tipo, rubroKey, rg, ancestorKeys });
    for (const imp of rg.imputables) {
      rows.push({ kind: 'imputable', tipo, rubroKey, rg, imp, ancestorKeys });
    }
    rows.push(...flattenRubros(rg.children, tipo, [...ancestorKeys, rubroKey]));
  }
  return rows;
}

// ── Filtro recursivo para búsqueda ─────────────────────────────────────────

function filterRg(rg: RubroGroup, lower: string): RubroGroup | null {
  const rubroMatch = String(rg.nroRubro).includes(lower) ||
    (rg.noImputable?.nombreCuenta ?? '').toLowerCase().includes(lower) ||
    (rg.noImputable?.descripcioncta ?? '').toLowerCase().includes(lower);
  const matchImps = rg.imputables.filter((i) =>
    i.codImputable.toLowerCase().includes(lower) || i.nombre.toLowerCase().includes(lower)
  );
  const matchChildren = rg.children.map((c) => filterRg(c, lower)).filter((c): c is RubroGroup => c !== null);
  if (!rubroMatch && !matchImps.length && !matchChildren.length) return null;
  return { ...rg, imputables: rubroMatch ? rg.imputables : matchImps, children: matchChildren };
}

function filterHierarchy(hierarchy: TipoGroup[], term: string): TipoGroup[] {
  if (!term) return hierarchy;
  const lower = term.toLowerCase();
  return hierarchy.map((tg) => {
    const tipoMatch = tg.tipo.includes(lower) || tg.label.toLowerCase().includes(lower);
    const rubros = tipoMatch ? tg.rubros : tg.rubros.map((r) => filterRg(r, lower)).filter((r): r is RubroGroup => r !== null);
    return { ...tg, rubros };
  }).filter((tg) => tg.rubros.length > 0);
}

/** Sin cuenta superior configurada (campo «Totaliza en» vacío o cero). Solo aplica si existe fila en NoImputables. */
function sinTotalizaEn(ni: NoImputable | undefined): boolean {
  if (!ni) return false;
  const t = ni.totalizaEn;
  return t == null || Number(t) === 0;
}

function rubrosSinTotalizaEnList(noImputables: NoImputable[]): NoImputable[] {
  return [...noImputables]
    .filter((n) => sinTotalizaEn(n))
    .sort((a, b) => a.nroRubro - b.nroRubro);
}

/** Preferencia de panel: por defecto contraído; si existe `'true'` en localStorage, expandido. */
const LS_PLAN_CUENTAS_PANEL_SIN_TOTALIZADOR = 'eOne.contabilidad.planCuentas.panelRubrosSinTotalizador.expandido';

function readPanelSinTotalizadorExpandido(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LS_PLAN_CUENTAS_PANEL_SIN_TOTALIZADOR) === 'true';
  } catch {
    return false;
  }
}

function writePanelSinTotalizadorExpandido(expandido: boolean): void {
  try {
    window.localStorage.setItem(LS_PLAN_CUENTAS_PANEL_SIN_TOTALIZADOR, expandido ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

interface PanelRubrosSinTotalizarProps {
  items: NoImputable[];
  onEdit: (nroRubro: number) => void;
  compact?: boolean;
}

const PanelRubrosSinTotalizar: React.FC<PanelRubrosSinTotalizarProps> = ({ items, onEdit, compact }) => {
  const [expandido, setExpandido] = useState(() => readPanelSinTotalizadorExpandido());

  const togglePanel = () => {
    setExpandido((prev) => {
      const next = !prev;
      writePanelSinTotalizadorExpandido(next);
      return next;
    });
  };

  if (items.length === 0) return null;

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberIcon fontSize="inherit" />}
      sx={{
        mb: 2,
        borderRadius: 2,
        maxWidth: '100%',
        overflow: 'hidden',
        alignItems: 'flex-start',
        '& .MuiAlert-message': {
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
          py: 0.5,
        },
        '& .MuiAlert-action': { pt: 0.5, alignItems: 'flex-start', ml: 0.5 },
      }}
      action={
        <Tooltip title={expandido ? 'Contraer' : 'Expandir'}>
          <IconButton size="small" color="inherit" onClick={togglePanel} aria-expanded={expandido} aria-label={expandido ? 'Contraer aviso de rubros sin totalizador' : 'Expandir aviso de rubros sin totalizador'}>
            {expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      }
    >
      <Box onClick={togglePanel} sx={{ cursor: 'pointer', maxWidth: '100%' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: expandido ? 0.5 : 0 }}>
          Rubros sin totalizador ({items.length})
        </Typography>
      </Box>
      <Collapse in={expandido} timeout="auto" unmountOnExit={false}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5, mt: 0.5, maxWidth: '100%', wordBreak: 'break-word' }}>
          No tienen configurado «Totaliza en». Puede incluir cuentas raíz correctas; revise las que deban depender de otro rubro.
        </Typography>
        {compact ? (
          <Stack spacing={0.5} sx={{ maxWidth: '100%' }}>
            {items.map((n) => (
              <Link key={n.nroRubro} component="button" type="button" onClick={() => onEdit(n.nroRubro)}
                sx={{
                  fontSize: '0.85rem',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  border: 0,
                  bgcolor: 'transparent',
                  p: 0,
                  color: 'primary.main',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}>
                {formatNoImputableLabel(n)}
              </Link>
            ))}
          </Stack>
        ) : (
          <TableContainer
            sx={{
              maxHeight: 220,
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
              overflowY: 'auto',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.light',
              bgcolor: 'rgba(255,255,255,0.6)',
            }}
          >
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 0.75, width: 72 }}>Nro.</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 0.75, width: 'auto' }}>Rubro</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 0.75, width: 52 }}> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((n) => (
                  <TableRow key={n.nroRubro} hover>
                    <TableCell sx={{ fontFamily: 'monospace', py: 0.5, verticalAlign: 'top' }}>{n.nroRubro}</TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        verticalAlign: 'top',
                      }}
                    >
                      {(n.nombreCuenta || n.descripcioncta || '').trim() || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'top' }}>
                      <Tooltip title="Editar rubro">
                        <IconButton size="small" color="primary" onClick={() => onEdit(n.nroRubro)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Alert>
  );
};

// ── Componente ─────────────────────────────────────────────────────────────

type ImputableDrawerState = {
  open: boolean;
  codImputable?: string | null;
  nroRubroPreset?: number | null;
  tipoPreset?: string | null;
};

type NoImputableDrawerState = {
  open: boolean;
  nroRubro?: number | null;
  totalizaEnPreset?: number | null;
};

const PlanCuentas: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [hierarchy, setHierarchy] = useState<TipoGroup[]>([]);
  const [noImputablesSnapshot, setNoImputablesSnapshot] = useState<NoImputable[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedTipos, setCollapsedTipos] = useState<Set<string>>(new Set());
  const [collapsedRubros, setCollapsedRubros] = useState<Set<string>>(new Set());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [openImportar, setOpenImportar] = useState(false);
  const [imputableDrawer, setImputableDrawer] = useState<ImputableDrawerState>({ open: false });
  const [noImputableDrawer, setNoImputableDrawer] = useState<NoImputableDrawerState>({ open: false });
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;

  const openNuevoImputable = (nroRubro?: number, tipo?: string) =>
    setImputableDrawer({
      open: true,
      codImputable: null,
      nroRubroPreset: nroRubro ?? null,
      tipoPreset: tipo ?? null,
    });

  const openEditarImputable = (codImputable: string) =>
    setImputableDrawer({ open: true, codImputable });

  const openNuevoNoImputable = (totalizaEn?: number) =>
    setNoImputableDrawer({
      open: true,
      nroRubro: null,
      totalizaEnPreset: totalizaEn ?? null,
    });

  const openEditarNoImputable = (nroRubro: number) =>
    setNoImputableDrawer({ open: true, nroRubro });

  const handleExportExcel = async () => {
    try {
      await exportPlanCuentasExcel(searchTerm);
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [imps, noImps] = await Promise.all([
        imputableService.getAllImputablesPlanCuentas(),
        noImputableService.getAllNoImputablesPlanCuentas(),
      ]);
      setNoImputablesSnapshot(noImps);
      setHierarchy(buildHierarchy(imps, noImps));
    } catch (err) {
      console.error('Error al cargar plan de cuentas:', err);
      enqueueSnackbar('Error al cargar el plan de cuentas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const toggleTipo = (t: string) =>
    setCollapsedTipos((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const toggleRubro = (k: string) =>
    setCollapsedRubros((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const expandirTodoPlan = () => {
    setCollapsedTipos(new Set());
    setCollapsedRubros(new Set());
  };

  const contraerTodoPlan = () => {
    const tipos = new Set(hierarchy.map((tg) => tg.tipo));
    const rubroKeys = new Set<string>();
    const walk = (rubrosTree: RubroGroup[], tipo: string) => {
      for (const rg of rubrosTree) {
        rubroKeys.add(`${tipo}-${rg.nroRubro}`);
        walk(rg.children, tipo);
      }
    };
    hierarchy.forEach((tg) => walk(tg.rubros, tg.tipo));
    setCollapsedTipos(tipos);
    setCollapsedRubros(rubroKeys);
  };

  const handleDeleteImputable = async (codImputable: string) => {
    if (!window.confirm('¿Está seguro de eliminar este imputable?')) return;
    try {
      await imputableService.deleteImputable(codImputable);
      enqueueSnackbar('Imputable eliminado exitosamente', { variant: 'success' });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar imputable', { variant: 'error' });
    }
  };

  const handleDeleteNoImputable = async (nroRubro: number) => {
    if (!window.confirm('¿Está seguro de eliminar este rubro?')) return;
    try {
      await noImputableService.deleteNoImputable(nroRubro);
      enqueueSnackbar('Rubro eliminado exitosamente', { variant: 'success' });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar rubro', { variant: 'error' });
    }
  };

  const handleClosePdfPreview = () => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleInformePdfPlanCuentas = async () => {
    try {
      setExportingPdf(true);
      const [rowsImp, rowsNoImp, companias] = await Promise.all([
        imputableService.getAllImputablesPlanCuentas(),
        noImputableService.getAllNoImputablesPlanCuentas(),
        companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }).catch(() => [])
      ]);

      if (!rowsImp.length && !rowsNoImp.length) {
        enqueueSnackbar('No hay datos para el informe.', { variant: 'warning' });
        return;
      }

      const empresa = companias[0];
      const empresaNombre = empresa?.nombre?.trim() || '—';
      const empresaLocalidad = empresa?.localidad?.trim() || '—';
      const empresaProvincia = empresa?.provincia?.trim() || '—';

      const noImpMap = new Map<number, NoImputable>(rowsNoImp.map((n) => [n.nroRubro, n]));
      const byRubro = new Map<number, typeof rowsImp>();
      for (const r of rowsImp) {
        const key = r.nroRubro ?? 0;
        const list = byRubro.get(key) ?? [];
        list.push(r);
        byRubro.set(key, list);
      }

      const rubroSet = new Set<number>();
      rowsNoImp.forEach((n) => rubroSet.add(n.nroRubro));
      rowsImp.forEach((r) => rubroSet.add(r.nroRubro ?? 0));
      const rubrosOrdenados = Array.from(rubroSet).sort((a, b) => a - b);

      const rubroBand: [number, number, number] = [236, 245, 255];
      type BodyEl = string | { content: string | number; styles?: Record<string, unknown> };
      type BodyRowKind = 'rubro' | 'cuenta';
      const body: BodyEl[][] = [];
      const bodyRowKinds: BodyRowKind[] = [];

      for (const rubro of rubrosOrdenados) {
        const ni = noImpMap.get(rubro);
        body.push([
          {
            content: String(rubro),
            styles: {
              fillColor: rubroBand,
              fontStyle: 'bold',
              fontSize: 9,
              halign: 'left' as const,
              cellPadding: 4
            }
          },
          {
            content: formatoNombreRubroReporte(ni, rubro),
            styles: { fillColor: rubroBand, fontStyle: 'bold', fontSize: 9, cellPadding: 4 }
          },
          { content: '', styles: { fillColor: rubroBand, cellPadding: 4 } }
        ]);
        bodyRowKinds.push('rubro');

        const imps = [...(byRubro.get(rubro) ?? [])].sort(
          (a, b) => Number(a.codImputable) - Number(b.codImputable)
        );

        for (const imp of imps) {
          body.push([
            imp.codImputable || '',
            (imp.nombre || '').trim(),
            visibleEnLabel(imp.visibleEn)
          ]);
          bodyRowKinds.push('cuenta');
        }
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 42;
      const marginBottom = 40;
      const headerTop = 38;
      const headerBlockHeight = 72;
      const marginTop = headerTop + headerBlockHeight;

      const today = new Date();
      const fechaStr = today.toLocaleDateString('es-AR');
      const horaStr = today.toLocaleTimeString('es-AR', { hour: 'numeric', minute: '2-digit', hour12: true });

      const metaHeader = () => {
        doc.setTextColor(0);
        doc.setFontSize(15);
        doc.text('Plan De Cuentas', marginX, headerTop);
        doc.setFontSize(10);
        doc.text(`Empresa: ${empresaNombre}`, marginX, headerTop + 20);
        doc.text(`Localidad: ${empresaLocalidad}`, marginX, headerTop + 34);
        doc.text(`Provincia: ${empresaProvincia}`, marginX, headerTop + 48);
      };

      const padBase = { top: 4, bottom: 4, right: 5 } as const;
      const padRubroCol0 = { ...padBase, left: 2 };
      const padCuentaCol0 = { ...padBase, left: 18 };

      autoTable(doc, {
        margin: { left: marginX, right: marginX, top: marginTop, bottom: marginBottom },
        showHead: 'everyPage',
        theme: 'plain',
        tableLineWidth: 0,
        styles: {
          fontSize: 9,
          cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
          valign: 'middle',
          overflow: 'linebreak',
          lineWidth: 0
        },
        headStyles: {
          fillColor: [245, 245, 248],
          textColor: [30, 30, 35],
          fontStyle: 'bold',
          fontSize: 9,
          lineWidth: 0
        },
        columnStyles: {
          0: { cellWidth: 96, halign: 'left' },
          1: { cellWidth: 312 },
          2: { cellWidth: 103, halign: 'left' }
        },
        head: [['Nro Cuenta', 'Descripcion', 'Visible en...']],
        body,
        tableWidth: 'wrap',
        didParseCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 0) return;
          const kind = bodyRowKinds[data.row.index];
          if (kind === 'rubro') {
            data.cell.styles.cellPadding = padRubroCol0;
          } else if (kind === 'cuenta') {
            data.cell.styles.cellPadding = padCuentaCol0;
          }
        },
        willDrawPage: () => {
          metaHeader();
        },
        didDrawPage: (data) => {
          const docRef = data.doc;
          const pageH =
            typeof docRef.internal.pageSize.getHeight === 'function'
              ? docRef.internal.pageSize.getHeight()
              : (docRef.internal.pageSize as unknown as { height: number }).height;
          docRef.setFontSize(8);
          docRef.setTextColor(90);
          docRef.text(`Fecha: ${fechaStr} Hora:${horaStr} Pagina ${data.pageNumber}`, marginX, pageH - marginBottom / 2);
          docRef.setTextColor(0);
        }
      });

      const pdfBlob = doc.output('blob');
      const blob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar PDF del plan de cuentas:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPdf(false);
    }
  };

  // Al buscar expandir todo
  const effTipos = searchTerm ? new Set<string>() : collapsedTipos;
  const effRubros = searchTerm ? new Set<string>() : collapsedRubros;
  const filtered = filterHierarchy(hierarchy, searchTerm);
  const sinTotalizar = rubrosSinTotalizaEnList(noImputablesSnapshot);

  const rubroName = (rg: RubroGroup) =>
    (rg.noImputable?.nombreCuenta || rg.noImputable?.descripcioncta || '').trim() || `Rubro ${rg.nroRubro}`;

  // Indentación basada en profundidad real del árbol
  const rubroPl = (depth: number) => `${8 + depth * LEVEL_PX}px`;
  const impPl   = (depth: number) => `${8 + (depth + 1) * LEVEL_PX + 8}px`;

  const overlays = (
    <>
      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Plan de cuentas"
          documentId="plan-cuentas-preview"
          documentType="recibo"
          downloadFileName="Plan de cuentas Report.pdf"
        />
      ) : null}
      <PlanCuentaImputableDrawer
        open={imputableDrawer.open}
        codImputable={imputableDrawer.codImputable}
        nroRubroPreset={imputableDrawer.nroRubroPreset}
        tipoPreset={imputableDrawer.tipoPreset}
        onClose={() => setImputableDrawer({ open: false })}
        onSaved={() => void loadData()}
      />
      <PlanCuentaNoImputableDrawer
        open={noImputableDrawer.open}
        nroRubro={noImputableDrawer.nroRubro}
        totalizaEnPreset={noImputableDrawer.totalizaEnPreset}
        onClose={() => setNoImputableDrawer({ open: false })}
        onSaved={() => void loadData()}
      />
      <PlanCuentasImportDialog
        open={openImportar}
        onClose={() => setOpenImportar(false)}
        onImported={() => void loadData()}
      />
    </>
  );

  // ── Mobile ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: '100%', overflow: 'hidden' }}>
          <CardContent sx={{ p: 2, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountTreeIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>Plan de Cuentas</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleInformePdfPlanCuentas}
                  disabled={exportingPdf}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  {exportingPdf ? 'Generando…' : 'Plan de cuentas'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => void handleExportExcel()}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Exportar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<FileUploadIcon />}
                  onClick={() => setOpenImportar(true)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Importar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={() => openNuevoNoImputable()}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Rubro
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => openNuevoImputable()}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Cuenta
                </Button>
              </Stack>
            </Stack>
            <TextField fullWidth placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#1976d2' }} /></InputAdornment> }}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8f9fa' } }}
            />
            {!loading && (
              <PanelRubrosSinTotalizar items={sinTotalizar} compact onEdit={openEditarNoImputable} />
            )}
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
            {!loading && filtered.map((tg) => {
              const tipoBg = TIPO_COLORS[tg.tipo] ?? '#f5f5f5';
              const tipoCollapsed = effTipos.has(tg.tipo);
              const flatRows = flattenRubros(tg.rubros, tg.tipo, []);
              return (
                <Box key={tg.tipo} sx={{ mb: 1 }}>
                  <Box onClick={() => toggleTipo(tg.tipo)} sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: tipoBg, cursor: 'pointer', mb: 0.5 }}>
                    {tipoCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, ml: 0.5 }}>{tg.label.toUpperCase()}</Typography>
                  </Box>
                  {!tipoCollapsed && flatRows.map((row) => {
                    const ancestorHidden = row.ancestorKeys.some((k) => effRubros.has(k));
                    if (ancestorHidden) return null;
                    if (row.kind === 'rubro') {
                      const isCollapsed = effRubros.has(row.rubroKey);
                      const ni = row.rg.noImputable;
                      return (
                        <Box key={row.rubroKey} sx={{ ml: `${row.rg.treeDepth * 12}px` }}>
                          <Stack direction="row" alignItems="center" sx={{ p: 0.75, borderRadius: 1, bgcolor: '#f0f4fa', mb: 0.25 }}>
                            <Box onClick={() => toggleRubro(row.rubroKey)} sx={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}>
                              {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              <Typography variant="body2" sx={{ fontWeight: 700, ml: 0.5, fontFamily: 'monospace' }}>{row.rg.nroRubro}</Typography>
                              <Typography variant="body2" sx={{ ml: 1, color: '#444' }}>{rubroName(row.rg)}</Typography>
                              {sinTotalizaEn(row.rg.noImputable) && (
                                <Chip label="Sin totalizar" size="small" color="warning" sx={{ ml: 1, height: 22, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Nueva cuenta imputable en este rubro">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNuevoImputable(row.rg.nroRubro, tg.tipo);
                                  }}
                                >
                                  <PostAddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Nuevo no imputable que totaliza en este rubro">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNuevoNoImputable(row.rg.nroRubro);
                                  }}
                                >
                                  <CreateNewFolderIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {ni && (
                                <>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditarNoImputable(ni.nroRubro); }} color="primary"><EditIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteNoImputable(ni.nroRubro); }} color="error"><DeleteIcon fontSize="small" /></IconButton>
                                </>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    }
                    if (effRubros.has(row.rubroKey)) return null;
                    const imp = row.imp!;
                    return (
                      <Card key={`imp-${imp.codImputable}`} variant="outlined" sx={{ ml: `${(row.rg.treeDepth + 1) * 12 + 8}px`, mb: 0.25, p: 0.75 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{imp.codImputable} — {imp.nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">Visible en: {visibleEnLabel(imp.visibleEn)}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => openEditarImputable(imp.codImputable)} color="primary"><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteImputable(imp.codImputable)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                          </Stack>
                        </Stack>
                      </Card>
                    );
                  })}
                </Box>
              );
            })}
            {!loading && filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {searchTerm ? `Sin resultados para "${searchTerm}"` : 'No hay cuentas cargadas'}
              </Typography>
            )}
          </CardContent>
        </Card>
        {overlays}
      </Box>
    );
  }

  // ── Desktop ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden', maxWidth: '100%' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountTreeIcon sx={{ color: '#1976d2', fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>Plan de Cuentas</Typography>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleInformePdfPlanCuentas}
                disabled={exportingPdf}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                {exportingPdf ? 'Generando…' : 'Plan de cuentas'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Exportar
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Importar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CreateNewFolderIcon />}
                onClick={() => openNuevoNoImputable()}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Nuevo rubro
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PostAddIcon />}
                onClick={() => openNuevoImputable()}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Nueva cuenta
              </Button>
            </Stack>
          </Stack>
          <Box sx={{ mb: 3 }}>
            <TextField fullWidth placeholder="Buscar por tipo, rubro, código o nombre..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#1976d2' }} /></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8f9fa' } }}
            />
          </Box>

          {!loading && (
            <PanelRubrosSinTotalizar items={sinTotalizar} onEdit={openEditarNoImputable} />
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }} color="text.secondary">Cargando plan de cuentas…</Typography>
            </Box>
          )}

          {!loading && (
            <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', maxWidth: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, width: '17%', maxWidth: 0, verticalAlign: 'middle' }}>
                      <Stack direction="row" alignItems="center" spacing={0.25} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                        <Tooltip title="Expandir todo">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); expandirTodoPlan(); }}
                              aria-label="Expandir todo el plan de cuentas"
                              disabled={!hierarchy.length}
                            >
                              <UnfoldMoreIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Contraer todo">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); contraerTodoPlan(); }}
                              aria-label="Contraer todo el plan de cuentas"
                              disabled={!hierarchy.length}
                            >
                              <UnfoldLessIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 600, ml: 0.25 }}>
                          Código / Nro.
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '58%' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '10%' }}>Visible en</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '15%', maxWidth: 0, verticalAlign: 'middle' }}>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                        <Tooltip title="Nueva cuenta imputable">
                          <PostAddIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        </Tooltip>
                        <Tooltip title="Nuevo no imputable">
                          <CreateNewFolderIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                        </Tooltip>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                          Acciones
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((tg) => {
                    const tipoBg = TIPO_COLORS[tg.tipo] ?? '#f5f5f5';
                    const tipoCollapsed = effTipos.has(tg.tipo);
                    const flatRows = flattenRubros(tg.rubros, tg.tipo, []);

                    return (
                      <React.Fragment key={tg.tipo}>
                        {/* ── TIPO ── */}
                        <TableRow onClick={() => toggleTipo(tg.tipo)} sx={{ bgcolor: tipoBg, cursor: 'pointer', '&:hover': { filter: 'brightness(0.97)' } }}>
                          <TableCell colSpan={4} sx={{ py: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {tipoCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.3 }}>
                                {tg.label.toUpperCase()}
                              </Typography>
                              <Chip label={tg.tipo} size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem', bgcolor: 'rgba(0,0,0,0.07)' }} />
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {!tipoCollapsed && flatRows.map((row) => {
                          // Ocultar si algún ancestro está colapsado
                          if (row.ancestorKeys.some((k) => effRubros.has(k))) return null;

                          if (row.kind === 'rubro') {
                            const isCollapsed = effRubros.has(row.rubroKey);
                            const hasContent = row.rg.imputables.length > 0 || row.rg.children.length > 0;
                            const ni = row.rg.noImputable;
                            return (
                              <TableRow key={row.rubroKey} onClick={() => toggleRubro(row.rubroKey)}
                                sx={{ bgcolor: '#f0f4fa', cursor: 'pointer', '&:hover': { bgcolor: '#e8eef7' } }}
                              >
                                <TableCell sx={{ py: 0.75, pl: rubroPl(row.rg.treeDepth) }}>
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {hasContent
                                      ? (isCollapsed ? <ChevronRightIcon sx={{ fontSize: 15, color: '#777' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#777' }} />)
                                      : <Box sx={{ width: 15 }} />}
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#333' }}>
                                      {row.rg.nroRubro}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, maxWidth: 0 }} colSpan={2}>
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                    <Tooltip title={rubroName(row.rg)} enterDelay={400}>
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#444',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          minWidth: 0,
                                          flex: '1 1 auto',
                                        }}
                                      >
                                        {rubroName(row.rg)}
                                      </Typography>
                                    </Tooltip>
                                    {sinTotalizaEn(ni) && (
                                      <Chip label="Sin totalizar" size="small" color="warning" sx={{ height: 22, fontSize: '0.65rem', flexShrink: 0 }} />
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }} onClick={(e) => e.stopPropagation()}>
                                  <Tooltip title="Nueva cuenta imputable en este rubro">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => openNuevoImputable(row.rg.nroRubro, tg.tipo)}
                                    >
                                      <PostAddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Nuevo no imputable que totaliza en este rubro">
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => openNuevoNoImputable(row.rg.nroRubro)}
                                    >
                                      <CreateNewFolderIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {ni && (
                                    <>
                                      <Tooltip title="Editar rubro">
                                        <IconButton size="small" onClick={() => openEditarNoImputable(ni.nroRubro)} color="primary">
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Eliminar rubro">
                                        <IconButton size="small" onClick={() => handleDeleteNoImputable(ni.nroRubro)} color="error">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }

                          // imputable: ocultar si su rubro directo está colapsado
                          if (effRubros.has(row.rubroKey)) return null;
                          const imp = row.imp!;
                          return (
                            <TableRow key={`imp-${imp.codImputable}`} hover sx={{ '&:hover': { bgcolor: '#fafcff' } }}>
                              <TableCell sx={{ py: 0.5, pl: impPl(row.rg.treeDepth) }}>
                                <Typography variant="body2" sx={{ color: '#333', fontFamily: 'monospace' }}>{imp.codImputable}</Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, maxWidth: 0 }}>
                                <Tooltip title={imp.nombre} enterDelay={400}>
                                  <Typography
                                    variant="body2"
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {imp.nombre}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, maxWidth: 0, width: '10%' }}>
                                <Tooltip title={visibleEnLabel(imp.visibleEn)} enterDelay={400}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {visibleEnLabel(imp.visibleEn)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5 }}>
                                <Tooltip title="Editar">
                                  <IconButton size="small" onClick={() => openEditarImputable(imp.codImputable)} color="primary">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" onClick={() => handleDeleteImputable(imp.codImputable)} color="error">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        {searchTerm ? `Sin resultados para "${searchTerm}"` : 'No hay cuentas cargadas'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {overlays}
    </Box>
  );
};

export default PlanCuentas;
